(() => {
  'use strict';

  const parseJSON = (element) => {
    if (!element) return null;

    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.warn(
        'Kawaii Stop PDP: product data could not be read.',
        error,
      );

      return null;
    }
  };

  const cssEscape = (value) => {
    if (
      window.CSS
      && typeof window.CSS.escape === 'function'
    ) {
      return window.CSS.escape(String(value));
    }

    return String(value).replace(
      /[^a-zA-Z0-9_-]/g,
      '\\$&',
    );
  };

  const formatMoney = (
    cents,
    format,
    currency,
  ) => {
    if (
      window.Shopify
      && typeof window.Shopify.formatMoney === 'function'
    ) {
      return window.Shopify.formatMoney(
        cents,
        format,
      );
    }

    try {
      return new Intl.NumberFormat(
        document.documentElement.lang || 'en-US',
        {
          style: 'currency',
          currency: currency || 'USD',
        },
      ).format(
        Number(cents || 0) / 100,
      );
    } catch (_error) {
      return `$${(
        Number(cents || 0) / 100
      ).toFixed(2)}`;
    }
  };

  const addBusinessDays = (
    date,
    numberOfDays,
  ) => {
    const result = new Date(date);

    let remaining = Math.max(
      0,
      Math.floor(
        Number(numberOfDays) || 0,
      ),
    );

    while (remaining > 0) {
      result.setDate(
        result.getDate() + 1,
      );

      const weekday = result.getDay();

      if (
        weekday !== 0
        && weekday !== 6
      ) {
        remaining -= 1;
      }
    }

    return result;
  };

  const addCalendarDays = (
    date,
    numberOfDays,
  ) => {
    const result = new Date(date);

    result.setDate(
      result.getDate()
        + Math.max(
          0,
          Math.floor(
            Number(numberOfDays) || 0,
          ),
        ),
    );

    return result;
  };

  const formatDeliveryDate = (
    date,
    includeYear = false,
  ) => {
    try {
      return new Intl.DateTimeFormat(
        document.documentElement.lang || 'en-US',
        {
          month: 'short',
          day: 'numeric',
          ...(includeYear
            ? { year: 'numeric' }
            : {}),
        },
      ).format(date);
    } catch (_error) {
      return `${
        date.getMonth() + 1
      }/${date.getDate()}`;
    }
  };

  const formatDeliveryRange = (
    startDate,
    endDate,
  ) => {
    const sameDay =
      startDate.getFullYear()
        === endDate.getFullYear()
      && startDate.getMonth()
        === endDate.getMonth()
      && startDate.getDate()
        === endDate.getDate();

    if (sameDay) {
      return formatDeliveryDate(
        startDate,
      );
    }

    const sameYear =
      startDate.getFullYear()
      === endDate.getFullYear();

    const sameMonth =
      sameYear
      && startDate.getMonth()
        === endDate.getMonth();

    if (sameMonth) {
      return `${formatDeliveryDate(
        startDate,
      )}–${endDate.getDate()}`;
    }

    return [
      formatDeliveryDate(
        startDate,
        !sameYear,
      ),
      formatDeliveryDate(
        endDate,
        !sameYear,
      ),
    ].join('–');
  };

  const setHidden = (
    element,
    hidden,
  ) => {
    if (!element) return;

    element.hidden = hidden;

    element.classList.toggle(
      'is-hidden',
      hidden,
    );
  };

  const cleanOptionValue = (value) =>
    String(value || '')
      .replace(
        /\s*\(old\)\s*/gi,
        '',
      )
      .trim();

  const normalizeOptionValue = (value) =>
    cleanOptionValue(value)
      .toLowerCase()
      .replace(
        /[^a-z0-9]+/g,
        '',
      );

  const normalizeText = (value) =>
    String(value || '')
      .replace(/\s+/g, ' ')
      .trim();

  const descriptionFactDefinitions = [
    {
      label: 'Materials',
      pattern:
        /^(?:material|materials|fabric|fabric type)$/i,
    },
    {
      label: 'Construction',
      pattern:
        /^(?:construction|fabric construction|craft of weaving|weave|weaving|knit type)$/i,
    },
    {
      label: 'Included',
      pattern:
        /^(?:included|what is included|what's included|package includes|components)$/i,
    },
    {
      label: 'Fit',
      pattern:
        /^(?:fit|fit type|size & fit|size and fit)$/i,
    },
    {
      label: 'Care',
      pattern:
        /^(?:care|care instructions|washing instructions)$/i,
    },
    {
      label: 'Dimensions',
      pattern:
        /^(?:dimensions|measurements|product dimensions)$/i,
    },
  ];

  const normalizeReadableText = (
    value,
  ) =>
    normalizeText(
      String(value || '')
        .replace(/\u00a0/g, ' ')
        .replace(
          /([a-z0-9])([A-Z])/g,
          '$1 $2',
        )
        .replace(
          /([,;:])(?=\S)/g,
          '$1 ',
        ),
    ).replace(
      /\s+([,.;:])/g,
      '$1',
    );

  const canonicalFactLabel = (
    value,
  ) => {
    const label =
      normalizeReadableText(value)
        .replace(
          /[:\-–—]+$/g,
          '',
        )
        .trim();

    return (
      descriptionFactDefinitions.find(
        (definition) =>
          definition.pattern.test(
            label,
          ),
      ) || null
    );
  };

  const safeFactValue = (
    value,
  ) => {
    const text =
      normalizeReadableText(value)
        .replace(
          /^[:\-–—]\s*/,
          '',
        )
        .trim();

    if (
      !text
      || text.length > 160
      || text.split(/\s+/).length > 28
    ) {
      return '';
    }

    if (
      /^(?:click|learn more|read more|see more)\b/i.test(
        text,
      )
    ) {
      return '';
    }

    return text;
  };

  const extractDescriptionFacts = (
    description,
  ) => {
    if (!description) return [];

    const facts = new Map();

    const addFact = (
      rawLabel,
      rawValue,
    ) => {
      const definition =
        canonicalFactLabel(rawLabel);

      if (
        !definition
        || facts.has(
          definition.label,
        )
      ) {
        return;
      }

      const value =
        safeFactValue(rawValue);

      if (value) {
        facts.set(
          definition.label,
          value,
        );
      }
    };

    description
      .querySelectorAll('dt')
      .forEach((term) => {
        const value =
          term.nextElementSibling;

        if (
          value?.tagName === 'DD'
        ) {
          addFact(
            term.textContent,
            value.textContent,
          );
        }
      });

    description
      .querySelectorAll('tr')
      .forEach((row) => {
        const cells = [
          ...row.querySelectorAll(
            ':scope > th, :scope > td',
          ),
        ];

        if (cells.length < 2) {
          return;
        }

        addFact(
          cells[0].textContent,
          cells
            .slice(1)
            .map(
              (cell) =>
                cell.textContent,
            )
            .join(' '),
        );
      });

    const labelPattern =
      new RegExp(
        `(?:^|\\s)(materials?|fabric type|fabric construction|fabric|construction|craft of weaving|knit type|weaving|weave|what is included|what's included|package includes|included|components|size & fit|size and fit|fit type|fit|care instructions|washing instructions|care|product dimensions|dimensions|measurements)\\s*[:\\-–—]\\s*`,
        'gi',
      );

    description
      .querySelectorAll('p, li')
      .forEach((element) => {
        const text =
          normalizeReadableText(
            element.textContent,
          );

        const matches = [
          ...text.matchAll(
            labelPattern,
          ),
        ];

        if (matches.length) {
          matches.forEach(
            (match, index) => {
              const start =
                Number(
                  match.index || 0,
                )
                + match[0].length;

              const end =
                matches[index + 1]
                  ?.index
                ?? text.length;

              addFact(
                match[1],
                text.slice(
                  start,
                  end,
                ),
              );
            },
          );

          return;
        }

        const simpleMatch =
          text.match(
            /^([^:]{2,30})\s*:\s*(.+)$/,
          );

        if (simpleMatch) {
          addFact(
            simpleMatch[1],
            simpleMatch[2],
          );
        }
      });

    description
      .querySelectorAll(
        'h2, h3, h4, h5, h6',
      )
      .forEach((heading) => {
        const next =
          heading.nextElementSibling;

        if (
          next
          && !/^H[1-6]$/.test(
            next.tagName,
          )
        ) {
          addFact(
            heading.textContent,
            next.textContent,
          );
        }
      });

    return [
      ...facts.entries(),
    ].map(
      ([label, value]) => ({
        label,
        value,
      }),
    );
  };

  const initDescriptionFacts = (
    root,
  ) => {
    const glance =
      root.querySelector(
        '[data-ks-glance]',
      );

    const list =
      glance?.querySelector(
        '[data-ks-glance-list]',
      );

    const description =
      root.querySelector(
        '[data-ks-product-description]',
      );

    if (
      !glance
      || !list
      || !description
    ) {
      return;
    }

    const existingLabels =
      new Set(
        [
          ...list.querySelectorAll(
            'dt',
          ),
        ].map(
          (term) =>
            normalizeText(
              term.textContent,
            ).toLowerCase(),
        ),
      );

    let factCount =
      list.querySelectorAll(
        '[data-ks-glance-fact]',
      ).length;

    const appendFact = (
      label,
      value,
    ) => {
      if (
        factCount >= 4
        || existingLabels.has(
          label.toLowerCase(),
        )
      ) {
        return false;
      }

      const row =
        document.createElement(
          'div',
        );

      row.dataset.ksGlanceFact =
        '';

      const term =
        document.createElement(
          'dt',
        );

      const definition =
        document.createElement(
          'dd',
        );

      term.textContent = label;
      definition.textContent =
        value;

      row.append(
        term,
        definition,
      );

      list.append(row);

      existingLabels.add(
        label.toLowerCase(),
      );

      factCount += 1;

      return true;
    };

    extractDescriptionFacts(
      description,
    ).forEach(
      ({ label, value }) => {
        appendFact(
          label,
          value,
        );
      },
    );

    const hasMeaningfulFact =
      factCount > 0;

    const productType =
      normalizeText(
        glance.dataset.productType,
      );

    if (
      hasMeaningfulFact
      && productType
      && factCount < 4
    ) {
      appendFact(
        'Product type',
        productType,
      );
    }

    glance.hidden =
      !hasMeaningfulFact;
  };

  const initRatingState = (
  root,
  signal,
) => {
  const row =
    root.querySelector(
      '[data-ks-rating-row]',
    );

  const app =
    root.querySelector(
      '[data-ks-rating-app]',
    );

  const link =
    root.querySelector(
      '[data-ks-reviews-link]',
    );

  if (
    !row
    || !app
    || !link
  ) {
    return;
  }

  /*
   * Ali Reviews owns the rating state.
   *
   * Do not infer zero reviews from its temporary loading
   * markup because the app can initially output zero/empty
   * content before hydrating the real product rating.
   */
  row.classList.remove(
    'is-zero-reviews',
  );

  link.textContent =
    'Read product reviews';

  link.setAttribute(
    'aria-label',
    'Read product reviews',
  );

  const sync = () => {
    const hasWidgetContent =
      app.childElementCount > 0
      || normalizeText(
        app.textContent || '',
      ).length > 0;

    row.classList.toggle(
      'is-review-widget-ready',
      hasWidgetContent,
    );

    /*
     * Never hide the Ali Reviews app or replace its result
     * with a guessed "new arrival" state.
     */
    row.classList.remove(
      'is-zero-reviews',
    );
  };

  sync();

  const observer =
    new MutationObserver(sync);

  observer.observe(app, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
  });

  const firstCheck =
    window.setTimeout(
      sync,
      500,
    );

  const secondCheck =
    window.setTimeout(
      sync,
      1600,
    );

  const thirdCheck =
    window.setTimeout(
      sync,
      4000,
    );

  signal.addEventListener(
    'abort',
    () => {
      observer.disconnect();

      window.clearTimeout(
        firstCheck,
      );

      window.clearTimeout(
        secondCheck,
      );

      window.clearTimeout(
        thirdCheck,
      );
    },
    { once: true },
  );
};


const initDeliveryTimeline = (
  root,
) => {
  const timeline =
    root.querySelector(
      '[data-ks-delivery]',
    );

  if (!timeline) return;

  const preparingOutput =
    timeline.querySelector(
      '[data-ks-delivery-preparing]',
    );

  const transitOutput =
    timeline.querySelector(
      '[data-ks-delivery-transit]',
    );

  const estimatedOutput =
    timeline.querySelector(
      '[data-ks-delivery-estimated]',
    );

  if (
    !preparingOutput
    || !transitOutput
    || !estimatedOutput
  ) {
    return;
  }

  const processingMin =
    Math.max(
      0,
      Number(
        timeline.dataset.processingMin,
      ) || 0,
    );

  const processingMax =
    Math.max(
      processingMin,
      Number(
        timeline.dataset.processingMax,
      ) || processingMin,
    );

  const deliveryMin =
    Math.max(
      1,
      Number(
        timeline.dataset.transitMin,
      ) || 1,
    );

  const deliveryMax =
    Math.max(
      deliveryMin,
      Number(
        timeline.dataset.transitMax,
      ) || deliveryMin,
    );

  const fulfillmentProfile =
    String(
      timeline.dataset.fulfillmentProfile
      || '',
    )
      .toLowerCase()
      .trim();

  const variableTimingProfiles =
    new Set([
      'extended_processing',
      'made_to_order',
      'preorder',
    ]);

  const today = new Date();

  today.setHours(
    12,
    0,
    0,
    0,
  );

  preparingOutput.textContent =
    formatDeliveryDate(today);

  if (
    variableTimingProfiles.has(
      fulfillmentProfile,
    )
  ) {
    transitOutput.textContent =
      fulfillmentProfile === 'preorder'
        ? 'Release date applies'
        : 'Production varies';

    estimatedOutput.textContent =
      'See product details';

    timeline.classList.add(
      'is-variable',
    );

    return;
  }

  timeline.classList.remove(
    'is-variable',
  );

  const shipByDate =
    addBusinessDays(
      today,
      processingMax,
    );

  const deliveryStart =
    addCalendarDays(
      today,
      deliveryMin,
    );

  const deliveryEnd =
    addCalendarDays(
      today,
      deliveryMax,
    );

  transitOutput.textContent =
    formatDeliveryDate(
      shipByDate,
    );

  estimatedOutput.textContent =
    formatDeliveryRange(
      deliveryStart,
      deliveryEnd,
    );
};

  const initBackInStockForm = (
    root,
    signal,
  ) => {
    const wrapper =
      root.querySelector(
        '[data-ks-back-in-stock-form]',
      );

    const fallback =
      root.querySelector(
        '[data-ks-back-in-stock-fallback]',
      );

    if (
      !wrapper
      || !fallback
    ) {
      return;
    }

    const hasInteractiveForm =
      () =>
        Boolean(
          wrapper.querySelector(
            'form, input[type="email"], iframe',
          ),
        );

    const sync = () => {
      const ready =
        hasInteractiveForm();

      wrapper.classList.toggle(
        'is-form-ready',
        ready,
      );

      if (ready) {
        fallback.hidden = true;
      }

      return ready;
    };

    sync();

    const observer =
      new MutationObserver(sync);

    observer.observe(
      wrapper,
      {
        childList: true,
        subtree: true,
      },
    );

    const fallbackTimer =
      window.setTimeout(
        () => {
          if (!sync()) {
            fallback.hidden =
              false;
          }
        },
        8000,
      );

    signal.addEventListener(
      'abort',
      () => {
        observer.disconnect();

        window.clearTimeout(
          fallbackTimer,
        );
      },
      { once: true },
    );
  };

  const initProduct = (
    root,
  ) => {
    if (
      !root
      || root.dataset
        .ksInitialized === 'true'
    ) {
      return;
    }

    const config =
      parseJSON(
        root.querySelector(
          '[data-ks-product-data]',
        ),
      );

    if (
      !config
      || !Array.isArray(
        config.variants,
      )
    ) {
      return;
    }

    root.dataset.ksInitialized =
      'true';

    const abortController =
      new AbortController();

    root.ksAbortController =
      abortController;

    const { signal } =
      abortController;

    const optionInputs = [
      ...root.querySelectorAll(
        '[data-ks-option-input]',
      ),
    ];

    const optionFieldsets = [
      ...root.querySelectorAll(
        '.ks-pdp__option',
      ),
    ];

    const internalOptionPositions =
      new Set(
        optionFieldsets
          .filter(
            (fieldset) =>
              fieldset.hasAttribute(
                'data-ks-internal-option',
              ),
          )
          .map(
            (fieldset) =>
              Number(
                fieldset.dataset
                  .optionPosition,
              ) - 1,
          ),
      );

    const variantIdInput =
      root.querySelector(
        '[data-ks-variant-id]',
      );

    const price =
      root.querySelector(
        '[data-ks-price]',
      );

    const comparePrice =
      root.querySelector(
        '[data-ks-compare-price]',
      );

    const savings =
      root.querySelector(
        '[data-ks-savings]',
      );

    const availability =
      root.querySelector(
        '[data-ks-availability]',
      );

    const availabilityLabel =
      root.querySelector(
        '[data-ks-availability-label]',
      );

    const addButton =
      root.querySelector(
        '[data-ks-add-button]',
      );

    const addButtonLabel =
      addButton?.querySelector(
        'span',
      );

    const purchaseRow =
      root.querySelector(
        '[data-ks-purchase-row]',
      );

    const backInStock =
      root.querySelector(
        '[data-ks-back-in-stock]',
      );

    const deliveryTimeline =
      root.querySelector(
        '[data-ks-delivery]',
      );

    const dynamicCheckout =
      root.querySelector(
        '.ks-pdp__dynamic-checkout',
      );

    const skuWrap =
      root.querySelector(
        '[data-ks-sku-wrap]',
      );

    const sku =
      root.querySelector(
        '[data-ks-sku]',
      );

    const mainImage =
      root.querySelector(
        '.ks-pdp__main-image',
      );

    const dynamicMedia =
      root.querySelector(
        '[data-ks-dynamic-media]',
      );

    const mediaStage =
      root.querySelector(
        '[data-ks-media-stage]',
      );

    const thumbs = [
      ...root.querySelectorAll(
        '[data-ks-media-thumb]',
      ),
    ];

    const imageThumbs =
      thumbs.filter(
        (thumb) =>
          thumb.dataset
            .mediaType === 'image',
      );

    const galleryCurrent =
      root.querySelector(
        '[data-ks-gallery-current]',
      );

    const galleryPrevious =
      root.querySelector(
        '[data-ks-gallery-previous]',
      );

    const galleryNext =
      root.querySelector(
        '[data-ks-gallery-next]',
      );

    const zoomDialog =
      root.querySelector(
        '[data-ks-zoom-dialog]',
      );

    const zoomImage =
      root.querySelector(
        '[data-ks-zoom-image]',
      );

    const zoomStage =
      root.querySelector(
        '[data-ks-zoom-stage]',
      );

    const zoomCurrent =
      root.querySelector(
        '[data-ks-zoom-current]',
      );

    const zoomTotal =
      root.querySelector(
        '[data-ks-zoom-total]',
      );

    const zoomButton =
      root.querySelector(
        '[data-ks-zoom-open]',
      );

    const zoomPrevious =
      root.querySelector(
        '[data-ks-zoom-previous]',
      );

    const zoomNext =
      root.querySelector(
        '[data-ks-zoom-next]',
      );

    try {
      initDescriptionFacts(root);
    } catch (error) {
      console.warn(
        'Kawaii Stop PDP: At-a-glance facts could not initialize.',
        error,
      );
    }

    try {
      initRatingState(
        root,
        signal,
      );
    } catch (error) {
      console.warn(
        'Kawaii Stop PDP: review state could not initialize.',
        error,
      );
    }

    try {
      initDeliveryTimeline(
        root,
      );
    } catch (error) {
      console.warn(
        'Kawaii Stop PDP: delivery timeline could not initialize.',
        error,
      );
    }

    try {
      initBackInStockForm(
        root,
        signal,
      );
    } catch (error) {
      console.warn(
        'Kawaii Stop PDP: back-in-stock form could not initialize.',
        error,
      );
    }

    let activeThumb =
      root.querySelector(
        '[data-ks-media-thumb].is-active',
      )
      || thumbs[0]
      || null;

    let activeImageData = null;
    let mediaRequestToken = 0;
    let zoomMagnified = false;

    const mediaPosition = (
      thumb,
    ) =>
      thumbs.indexOf(thumb);

    const imagePosition = (
      thumb,
    ) =>
      imageThumbs.indexOf(
        thumb,
      );

    const imageDataFromThumb = (
      thumb,
    ) => {
      if (
        !thumb
        || thumb.dataset
          .mediaType !== 'image'
        || !thumb.dataset.src
      ) {
        return null;
      }

      return {
        src:
          thumb.dataset.src,

        srcset:
          thumb.dataset.srcset
          || '',

        zoom:
          thumb.dataset.zoom
          || thumb.dataset.src,

        width:
          Number(
            thumb.dataset.width
            || 0,
          ),

        height:
          Number(
            thumb.dataset.height
            || 0,
          ),

        alt:
          thumb.dataset.alt
          || '',
      };
    };

    const updateMediaCounters =
      () => {
        const galleryIndex =
          activeThumb
            ? mediaPosition(
              activeThumb,
            )
            : -1;

        if (
          galleryCurrent
          && galleryIndex >= 0
        ) {
          galleryCurrent
            .textContent =
              String(
                galleryIndex + 1,
              );
        }

        const zoomIndex =
          activeThumb?.dataset
            .mediaType === 'image'
            ? imagePosition(
              activeThumb,
            )
            : -1;

        if (zoomCurrent) {
          zoomCurrent.textContent =
            String(
              zoomIndex >= 0
                ? zoomIndex + 1
                : 1,
            );
        }

        if (zoomTotal) {
          zoomTotal.textContent =
            String(
              Math.max(
                imageThumbs.length,
                1,
              ),
            );
        }
      };

    const setActiveThumb = (
      thumb,
      focus = false,
    ) => {
      thumbs.forEach(
        (item) => {
          const isActive =
            item === thumb;

          item.classList.toggle(
            'is-active',
            isActive,
          );

          item.setAttribute(
            'aria-current',
            isActive
              ? 'true'
              : 'false',
          );
        },
      );

      activeThumb =
        thumb || null;

      updateMediaCounters();

      if (
        focus
        && thumb
      ) {
        thumb.focus({
          preventScroll: true,
        });
      }
    };

    const clearDynamicMedia =
      () => {
        if (!dynamicMedia) {
          return;
        }

        dynamicMedia
          .replaceChildren();

        dynamicMedia.hidden =
          true;
      };

    const setZoomMagnified = (
      magnified,
    ) => {
      if (!zoomImage) return;

      zoomMagnified =
        Boolean(magnified);

      zoomImage.classList.toggle(
        'is-magnified',
        zoomMagnified,
      );

      zoomImage.setAttribute(
        'aria-pressed',
        zoomMagnified
          ? 'true'
          : 'false',
      );

      zoomImage.setAttribute(
        'aria-label',
        zoomMagnified
          ? 'Zoom out of product image'
          : 'Zoom in on product image',
      );

      if (
        !zoomMagnified
        && zoomStage
      ) {
        zoomStage.scrollTop = 0;
        zoomStage.scrollLeft = 0;
      }
    };

    const updateZoomImage = (
      imageData,
      position = 1,
    ) => {
      if (
        !zoomImage
        || !imageData?.src
      ) {
        return;
      }

      setZoomMagnified(false);

      zoomImage.src =
        imageData.zoom
        || imageData.src;

      zoomImage.alt =
        imageData.alt
        || mainImage?.alt
        || '';

      if (zoomCurrent) {
        zoomCurrent.textContent =
          String(
            Math.max(
              1,
              position,
            ),
          );
      }
    };

    const applyImageData = (
      imageData,
      thumb = null,
      focus = false,
    ) => {
      if (
        !mainImage
        || !imageData?.src
      ) {
        return false;
      }

      if (thumb) {
        setActiveThumb(
          thumb,
          focus,
        );
      } else {
        setActiveThumb(null);
      }

      clearDynamicMedia();

      if (zoomButton) {
        zoomButton.hidden =
          false;
      }

      mainImage.classList.remove(
        'is-hidden',
      );

      activeImageData =
        imageData;

      const requestToken =
        ++mediaRequestToken;

      mediaStage?.setAttribute(
        'aria-busy',
        'true',
      );

      mainImage.classList.add(
        'is-loading',
      );

      const finishLoad = () => {
        if (
          requestToken
          !== mediaRequestToken
        ) {
          return;
        }

        mediaStage
          ?.removeAttribute(
            'aria-busy',
          );

        mainImage.classList.remove(
          'is-loading',
        );
      };

      mainImage.addEventListener(
        'load',
        finishLoad,
        {
          once: true,
          signal,
        },
      );

      mainImage.addEventListener(
        'error',
        finishLoad,
        {
          once: true,
          signal,
        },
      );

      mainImage.alt =
        imageData.alt || '';

      if (
        imageData.width > 0
      ) {
        mainImage.width =
          imageData.width;
      }

      if (
        imageData.height > 0
      ) {
        mainImage.height =
          imageData.height;
      }

      if (imageData.srcset) {
        mainImage.srcset =
          imageData.srcset;
      } else {
        mainImage.removeAttribute(
          'srcset',
        );
      }

      mainImage.src =
        imageData.src;

      if (
        mediaStage
        && imageData.width > 0
        && imageData.height > 0
      ) {
        mediaStage.style
          .setProperty(
            '--ks-media-ratio',
            imageData.width
              / imageData.height,
          );
      }

      if (mainImage.complete) {
        window
          .requestAnimationFrame(
            finishLoad,
          );
      }

      const zoomIndex =
        thumb
          ? imagePosition(
            thumb,
          ) + 1
          : 1;

      if (zoomDialog?.open) {
        updateZoomImage(
          imageData,
          zoomIndex,
        );
      }

      root.dispatchEvent(
        new CustomEvent(
          'ks:media-change',
          {
            bubbles: true,
            detail: {
              mediaId:
                thumb?.dataset
                  .mediaId
                || null,
              image:
                imageData,
            },
          },
        ),
      );

      return true;
    };

    const selectMedia = (
      thumb,
      focus = false,
    ) => {
      if (!thumb) return;

      const type =
        thumb.dataset.mediaType;

      const mediaId =
        thumb.dataset.mediaId;

      if (type === 'image') {
        applyImageData(
          imageDataFromThumb(
            thumb,
          ),
          thumb,
          focus,
        );

        return;
      }

      setActiveThumb(
        thumb,
        focus,
      );

      activeImageData = null;

      mediaStage
        ?.removeAttribute(
          'aria-busy',
        );

      mainImage
        ?.classList.remove(
          'is-loading',
        );

      if (zoomButton) {
        zoomButton.hidden =
          true;
      }

      clearDynamicMedia();

      const template =
        root.querySelector(
          `[data-ks-media-template="${cssEscape(
            mediaId,
          )}"]`,
        );

      if (
        template
        && dynamicMedia
      ) {
        mainImage
          ?.classList.add(
            'is-hidden',
          );

        dynamicMedia.appendChild(
          template.content
            .cloneNode(true),
        );

        dynamicMedia.hidden =
          false;
      }
    };

    const stepMedia = (
      direction,
    ) => {
      if (thumbs.length < 2) {
        return;
      }

      const current =
        Math.max(
          0,
          mediaPosition(
            activeThumb,
          ),
        );

      const nextIndex =
        (
          current
          + direction
          + thumbs.length
        ) % thumbs.length;

      selectMedia(
        thumbs[nextIndex],
      );
    };

    const showZoomThumb = (
      thumb,
    ) => {
      const imageData =
        imageDataFromThumb(
          thumb,
        );

      if (!imageData) return;

      applyImageData(
        imageData,
        thumb,
      );

      updateZoomImage(
        imageData,
        imagePosition(thumb) + 1,
      );
    };

    const stepZoom = (
      direction,
    ) => {
      if (
        imageThumbs.length < 2
      ) {
        return;
      }

      const current =
        activeThumb?.dataset
          .mediaType === 'image'
          ? Math.max(
            0,
            imagePosition(
              activeThumb,
            ),
          )
          : 0;

      const nextIndex =
        (
          current
          + direction
          + imageThumbs.length
        ) % imageThumbs.length;

      showZoomThumb(
        imageThumbs[nextIndex],
      );
    };

    thumbs.forEach(
      (thumb) => {
        thumb.addEventListener(
          'click',
          () =>
            selectMedia(thumb),
          { signal },
        );
      },
    );

    galleryPrevious
      ?.addEventListener(
        'click',
        () => stepMedia(-1),
        { signal },
      );

    galleryNext
      ?.addEventListener(
        'click',
        () => stepMedia(1),
        { signal },
      );

    const selectedOptions =
      () => {
        const selected = [];

        optionInputs.forEach(
          (input) => {
            if (input.checked) {
              selected[
                Number(
                  input.dataset
                    .optionPosition,
                ) - 1
              ] = input.value;
            }
          },
        );

        return selected;
      };

    const updateOptionStates =
      () => {
        const current =
          selectedOptions();

        optionInputs.forEach(
          (input) => {
            const position =
              Number(
                input.dataset
                  .optionPosition,
              ) - 1;

            if (
              internalOptionPositions
                .has(position)
            ) {
              input.disabled =
                false;

              input.setAttribute(
                'aria-disabled',
                'false',
              );

              return;
            }

            const matchingVariants =
              config.variants.filter(
                (variant) =>
                  variant.options[
                    position
                  ] === input.value
                  && variant.options
                    .slice(
                      0,
                      position,
                    )
                    .every(
                      (
                        value,
                        index,
                      ) =>
                        internalOptionPositions
                          .has(index)
                        || current[
                          index
                        ] === undefined
                        || value
                          === current[
                            index
                          ],
                    ),
              );

            const exists =
              matchingVariants.length
              > 0;

            const hasAvailable =
              matchingVariants.some(
                (variant) =>
                  variant.available,
              );

            const choice =
              input.closest(
                '[data-ks-option-choice]',
              );

            input.disabled =
              !exists;

            input.setAttribute(
              'aria-disabled',
              exists
                ? 'false'
                : 'true',
            );

            choice?.classList
              .toggle(
                'is-unavailable',
                !exists,
              );

            choice?.classList
              .toggle(
                'is-sold-out',
                exists
                  && !hasAvailable,
              );
          },
        );
      };

    const updateSizeVisibility =
      () => {
        const selectedNonSizeValues =
          optionFieldsets
            .filter(
              (fieldset) =>
                !fieldset
                  .hasAttribute(
                    'data-ks-size-option',
                  )
                && !fieldset
                  .hasAttribute(
                    'data-ks-internal-option',
                  ),
            )
            .map(
              (fieldset) =>
                fieldset
                  .querySelector(
                    '[data-ks-option-input]:checked',
                  )
                  ?.value,
            )
            .filter(Boolean)
            .map(
              normalizeOptionValue,
            );

        const sizeIsIrrelevant =
          selectedNonSizeValues.some(
            (value) =>
              value
                === 'onlywig'
              || value
                === 'wigonly',
          );

        optionFieldsets
          .filter(
            (fieldset) =>
              fieldset.hasAttribute(
                'data-ks-size-option',
              ),
          )
          .forEach(
            (fieldset) => {
              fieldset.hidden =
                sizeIsIrrelevant;
            },
          );
      };

    const reconcileFollowingOptions =
      (changedInput) => {
        const changedPosition =
          Number(
            changedInput.dataset
              .optionPosition,
          ) - 1;

        const current =
          selectedOptions();

        const visibleCombinationExists =
          config.variants.some(
            (variant) =>
              variant.options.every(
                (
                  value,
                  index,
                ) =>
                  internalOptionPositions
                    .has(index)
                  || value
                    === current[
                      index
                    ],
              ),
          );

        if (
          visibleCombinationExists
        ) {
          return;
        }

        const compatibleVariants =
          config.variants.filter(
            (variant) =>
              variant.options
                .slice(
                  0,
                  changedPosition
                    + 1,
                )
                .every(
                  (
                    value,
                    index,
                  ) =>
                    internalOptionPositions
                      .has(index)
                    || current[
                      index
                    ] === undefined
                    || value
                      === current[
                        index
                      ],
                ),
          );

        const fallbackVariant =
          compatibleVariants.find(
            (variant) =>
              variant.available,
          )
          || compatibleVariants[0];

        if (!fallbackVariant) {
          return;
        }

        fallbackVariant.options
          .forEach(
            (value, index) => {
              if (
                index
                  <= changedPosition
                && !internalOptionPositions
                  .has(index)
              ) {
                return;
              }

              const matchingInput =
                optionInputs.find(
                  (input) =>
                    Number(
                      input.dataset
                        .optionPosition,
                    ) - 1
                      === index
                    && input.value
                      === value,
                );

              if (matchingInput) {
                matchingInput
                  .checked = true;
              }
            },
          );
      };

    const updateBackInStockVariant =
      (variant) => {
        if (
          !backInStock
          || !variant
        ) {
          return;
        }

        backInStock.dataset
          .variantId =
            String(variant.id);

        backInStock
          .querySelectorAll(
            'input[name="variant"], input[name="variant_id"], input[name*="[variant]"]',
          )
          .forEach((input) => {
            input.value =
              String(variant.id);

            input.dispatchEvent(
              new Event(
                'change',
                {
                  bubbles: true,
                },
              ),
            );
          });
      };

    const updatePurchaseState =
      (variant) => {
        const exists =
          Boolean(variant);

        const available =
          Boolean(
            variant?.available,
          );

        setHidden(
          purchaseRow,
          !available,
        );

        setHidden(
          backInStock,
          !exists || available,
        );

        setHidden(
          deliveryTimeline,
          !available,
        );

        setHidden(
          dynamicCheckout,
          !available,
        );

        updateBackInStockVariant(
          variant,
        );

        if (addButton) {
          addButton.disabled =
            !available;

          addButton.setAttribute(
            'aria-disabled',
            available
              ? 'false'
              : 'true',
          );
        }

        if (addButtonLabel) {
          addButtonLabel
            .textContent =
              exists
                ? available
                  ? config.addLabel
                  : config.soldOutLabel
                : config
                  .unavailableLabel;
        }
      };

    const setVariantFormValue = (
      variant,
      forceNotify = false,
    ) => {
      if (!variantIdInput) {
        return;
      }

      const nextValue =
        variant
          ? String(variant.id)
          : '';

      const valueChanged =
        variantIdInput.value
        !== nextValue;

      if (valueChanged) {
        variantIdInput.value =
          nextValue;
      }

      if (
        !valueChanged
        && !forceNotify
      ) {
        return;
      }

      variantIdInput
        .dispatchEvent(
          new Event(
            'input',
            {
              bubbles: true,
            },
          ),
        );

      variantIdInput
        .dispatchEvent(
          new Event(
            'change',
            {
              bubbles: true,
            },
          ),
        );
    };

    const updateVariantURL = (
      variant,
    ) => {
      if (!variant) return;

      const url =
        new URL(
          window.location.href,
        );

      url.searchParams.set(
        'variant',
        variant.id,
      );

      window.history
        .replaceState(
          {
            ...window.history.state,
            ksVariantId:
              variant.id,
          },
          '',
          url.toString(),
        );
    };

    const integrationSelectedOptions =
      () =>
        optionFieldsets
          .filter(
            (fieldset) =>
              !fieldset
                .hasAttribute(
                  'data-ks-internal-option',
                ),
          )
          .map((fieldset) => {
            const input =
              fieldset.querySelector(
                '[data-ks-option-input]:checked',
              );

            if (!input) {
              return null;
            }

            const label =
              normalizeText(
                fieldset
                  .querySelector(
                    'legend > span',
                  )
                  ?.textContent,
              )
              || fieldset.dataset
                .optionName
              || 'Option';

            return {
              name: label,
              value:
                cleanOptionValue(
                  input.value,
                ),
            };
          })
          .filter(Boolean);

    const dispatchVariantIntegrationEvents =
      (variant, source) => {
        if (!variant) return;

        const selectedOptions =
          integrationSelectedOptions();

        const productTitle =
          normalizeText(
            root
              .querySelector(
                '.ks-pdp__title',
              )
              ?.textContent,
          );

        const productHandle =
          window.location.pathname
            .split(
              '/products/',
            )[1]
            ?.split('/')[0]
          || '';

        const productId =
          Number(
            backInStock
              ?.dataset.productId
            || 0,
          )
          || backInStock
            ?.dataset.productId
          || '';

        const resolvedVariant = {
          id: variant.id,

          title:
            selectedOptions
              .map(
                (option) =>
                  option.value,
              )
              .join(' / '),

          availableForSale:
            Boolean(
              variant.available,
            ),

          price: {
            amount: (
              Number(
                variant.price
                || 0,
              ) / 100
            ).toFixed(2),

            currencyCode:
              config.currency
              || root.dataset
                .currency
              || 'USD',
          },

          selectedOptions,
        };

        const resolved = {
          variant:
            resolvedVariant,
        };

        const promise =
          Promise.resolve(
            resolved,
          );

        const product = {
          id: productId,
          title: productTitle,
          handle: productHandle,
        };

        const standardPayload = {
          selectedOptions,
          product,
          promise,
        };

        const StandardEvents =
          window.Shopify
            ?.StandardEvents;

        if (
          StandardEvents
            ?.ProductSelectEvent
        ) {
          root.dispatchEvent(
            new StandardEvents
              .ProductSelectEvent(
                standardPayload,
              ),
          );
        } else {
          const standardEvent =
            new CustomEvent(
              'shopify:product:select',
              {
                bubbles: true,
                detail:
                  standardPayload,
              },
            );

          Object.assign(
            standardEvent,
            standardPayload,
          );

          root.dispatchEvent(
            standardEvent,
          );
        }

        const detail = {
          variant,
          sectionId:
            root.dataset.sectionId,
          source,
        };

        root.dispatchEvent(
          new CustomEvent(
            'ks:variant-change',
            {
              bubbles: true,
              detail,
            },
          ),
        );

        root.dispatchEvent(
          new CustomEvent(
            'variant:change',
            {
              bubbles: true,
              detail,
            },
          ),
        );

        root.dispatchEvent(
          new CustomEvent(
            'product:variant-change',
            {
              bubbles: true,
              detail,
            },
          ),
        );
      };

    const syncVariantMedia = (
      variant,
    ) => {
      if (!variant) return;

      if (
        variant.featuredMediaId
      ) {
        const mediaThumb =
          root.querySelector(
            `[data-ks-media-thumb][data-media-id="${cssEscape(
              variant
                .featuredMediaId,
            )}"]`,
          );

        if (mediaThumb) {
          selectMedia(
            mediaThumb,
          );

          return;
        }
      }

      if (
        variant.featuredImage
          ?.src
      ) {
        applyImageData(
          variant.featuredImage,
        );
      }
    };

    const updateVariant = ({
      updateURL = true,
      source = 'selection',
      syncMedia = true,
    } = {}) => {
      const options =
        selectedOptions();

      const exactVariant =
        config.variants.find(
          (item) =>
            item.options.every(
              (
                value,
                index,
              ) =>
                value
                === options[index],
            ),
        );

      const visibleCandidates =
        internalOptionPositions.size
          ? config.variants
            .filter(
              (item) =>
                item.options.every(
                  (
                    value,
                    index,
                  ) =>
                    internalOptionPositions
                      .has(index)
                    || value
                      === options[
                        index
                      ],
                ),
            )
          : [];

      const variant =
        internalOptionPositions.size
          ? exactVariant?.available
            ? exactVariant
            : visibleCandidates
              .find(
                (item) =>
                  item.available,
              )
              || exactVariant
              || visibleCandidates[0]
          : exactVariant;

      if (variant) {
        internalOptionPositions
          .forEach(
            (position) => {
              const matchingInput =
                optionInputs.find(
                  (input) =>
                    Number(
                      input.dataset
                        .optionPosition,
                    ) - 1
                      === position
                    && input.value
                      === variant
                        .options[
                          position
                        ],
                );

              if (matchingInput) {
                matchingInput
                  .checked = true;
              }
            },
          );
      }

      optionInputs.forEach(
        (input) => {
          if (!input.checked) {
            return;
          }

          const output =
            root.querySelector(
              `[data-ks-selected-value="${cssEscape(
                input.dataset
                  .optionPosition,
              )}"]`,
            );

          if (output) {
            output.textContent =
              cleanOptionValue(
                input.value,
              );
          }
        },
      );

      updateOptionStates();
      updateSizeVisibility();

      if (!variant) {
        setVariantFormValue(
          null,
        );

        availability
          ?.classList.add(
            'is-sold-out',
          );

        if (
          availabilityLabel
        ) {
          availabilityLabel
            .textContent =
              config
                .unavailableLabel;
        }

        updatePurchaseState(
          null,
        );

        return null;
      }

      if (updateURL) {
        updateVariantURL(
          variant,
        );
      }

      setVariantFormValue(
        variant,
      );

      const formattedPrice =
        formatMoney(
          variant.price,
          config.moneyFormat,
          config.currency,
        );

      if (price) {
        price.innerHTML =
          formattedPrice;
      }

      const onSale =
        Number(
          variant.compareAtPrice
          || 0,
        )
        > Number(
          variant.price
          || 0,
        );

      if (comparePrice) {
        comparePrice.innerHTML =
          onSale
            ? formatMoney(
              variant
                .compareAtPrice,
              config.moneyFormat,
              config.currency,
            )
            : '';
      }

      if (savings) {
        savings.innerHTML =
          onSale
            ? `${
              config.saveLabel
            } ${formatMoney(
              variant
                .compareAtPrice
              - variant.price,
              config.moneyFormat,
              config.currency,
            )}`
            : '';
      }

      setHidden(
        comparePrice,
        !onSale,
      );

      setHidden(
        savings,
        !onSale,
      );

      availability
        ?.classList.toggle(
          'is-sold-out',
          !variant.available,
        );

      if (
        availabilityLabel
      ) {
        availabilityLabel
          .textContent =
            variant.available
              ? config
                .availableLabel
              : config
                .soldOutLabel;
      }

      updatePurchaseState(
        variant,
      );

      if (sku) {
        sku.textContent =
          variant.sku || '';
      }

      setHidden(
        skuWrap,
        !variant.sku,
      );

      if (syncMedia) {
        syncVariantMedia(
          variant,
        );
      }

      /*
       * Omnisend's variant-specific Back in Stock form resolves
       * against the exact variant URL. Re-announce the final state
       * only after the URL, form value, availability panel and app
       * block are synchronized.
       */
      window
        .requestAnimationFrame(
          () => {
            setVariantFormValue(
              variant,
              true,
            );

            dispatchVariantIntegrationEvents(
              variant,
              source,
            );
          },
        );

      return variant;
    };

    const selectVariantById = (
      variantId,
      source = 'history',
    ) => {
      const variant =
        config.variants.find(
          (item) =>
            String(item.id)
            === String(
              variantId || '',
            ),
        );

      if (!variant) {
        return false;
      }

      variant.options.forEach(
        (value, index) => {
          const input =
            optionInputs.find(
              (optionInput) =>
                Number(
                  optionInput
                    .dataset
                    .optionPosition,
                ) - 1
                  === index
                && optionInput
                  .value
                  === value,
            );

          if (input) {
            input.checked =
              true;
          }
        },
      );

      updateVariant({
        updateURL: false,
        source,
        syncMedia: true,
      });

      return true;
    };

    optionInputs.forEach(
      (input) => {
        input.addEventListener(
          'change',
          () => {
            reconcileFollowingOptions(
              input,
            );

            updateVariant();
          },
          { signal },
        );
      },
    );

    const initialVariantId =
      new URL(
        window.location.href,
      ).searchParams.get(
        'variant',
      );

    if (
      !initialVariantId
      || !selectVariantById(
        initialVariantId,
        'initial-url',
      )
    ) {
      updateVariant({
        updateURL: false,
        source: 'initial',
        syncMedia: false,
      });
    }

    window.addEventListener(
      'popstate',
      () => {
        const variantId =
          new URL(
            window.location.href,
          ).searchParams.get(
            'variant',
          );

        if (variantId) {
          selectVariantById(
            variantId,
          );

          return;
        }

        const fallbackVariant =
          config.variants.find(
            (variant) =>
              variant.available,
          )
          || config.variants[0];

        if (fallbackVariant) {
          fallbackVariant.options
            .forEach(
              (
                value,
                index,
              ) => {
                const input =
                  optionInputs.find(
                    (
                      optionInput,
                    ) =>
                      Number(
                        optionInput
                          .dataset
                          .optionPosition,
                      ) - 1
                        === index
                      && optionInput
                        .value
                        === value,
                  );

                if (input) {
                  input.checked =
                    true;
                }
              },
            );

          updateVariant({
            updateURL: false,
            source:
              'history-default',
            syncMedia: false,
          });
        }

        const featuredThumb =
          config
            .productFeaturedMediaId
            ? root.querySelector(
              `[data-ks-media-thumb][data-media-id="${cssEscape(
                config
                  .productFeaturedMediaId,
              )}"]`,
            )
            : null;

        if (featuredThumb) {
          selectMedia(
            featuredThumb,
          );
        }
      },
      { signal },
    );

    window.addEventListener(
      'pageshow',
      (event) => {
        if (!event.persisted) {
          return;
        }

        const variantId =
          new URL(
            window.location.href,
          ).searchParams.get(
            'variant',
          );

        if (variantId) {
          selectVariantById(
            variantId,
            'page-restore',
          );
        }
      },
      { signal },
    );

    root
      .querySelectorAll(
        '[data-ks-quantity-minus], [data-ks-quantity-plus]',
      )
      .forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            const quantity =
              button
                .closest(
                  '.ks-pdp__quantity',
                )
                ?.querySelector(
                  'input[type="number"]',
                );

            if (!quantity) {
              return;
            }

            const current =
              Number(
                quantity.value
                || 1,
              );

            quantity.value =
              String(
                Math.max(
                  1,
                  current
                    + (
                      button
                        .hasAttribute(
                          'data-ks-quantity-plus',
                        )
                        ? 1
                        : -1
                    ),
                ),
              );

            quantity
              .dispatchEvent(
                new Event(
                  'change',
                  {
                    bubbles: true,
                  },
                ),
              );
          },
          { signal },
        );
      });

    root
      .querySelectorAll(
        '[data-ks-size-link]',
      )
      .forEach((link) => {
        link.addEventListener(
          'click',
          () => {
            const target =
              document.querySelector(
                link.getAttribute(
                  'href',
                ),
              );

            if (
              target?.tagName
              === 'DETAILS'
            ) {
              target.open = true;
            }
          },
          { signal },
        );
      });

    const openZoom = () => {
      if (
        !zoomDialog
        || !zoomImage
        || !mainImage
        || mainImage
          .classList.contains(
            'is-hidden',
          )
      ) {
        return;
      }

      const imageData =
        activeThumb?.dataset
          .mediaType === 'image'
          ? imageDataFromThumb(
            activeThumb,
          )
          : activeImageData;

      if (!imageData) return;

      const position =
        activeThumb?.dataset
          .mediaType === 'image'
          ? imagePosition(
            activeThumb,
          ) + 1
          : 1;

      updateZoomImage(
        imageData,
        position,
      );

      if (
        typeof zoomDialog
          .showModal
        === 'function'
      ) {
        zoomDialog.showModal();

        window
          .requestAnimationFrame(
            () => {
              zoomImage.focus({
                preventScroll: true,
              });
            },
          );
      }
    };

    const toggleZoomMagnification =
      () => {
        setZoomMagnified(
          !zoomMagnified,
        );
      };

    zoomButton
      ?.addEventListener(
        'click',
        openZoom,
        { signal },
      );

    root
      .querySelector(
        '[data-ks-zoom-close]',
      )
      ?.addEventListener(
        'click',
        () =>
          zoomDialog?.close(),
        { signal },
      );

    zoomPrevious
      ?.addEventListener(
        'click',
        () => stepZoom(-1),
        { signal },
      );

    zoomNext
      ?.addEventListener(
        'click',
        () => stepZoom(1),
        { signal },
      );

    zoomImage
      ?.addEventListener(
        'click',
        toggleZoomMagnification,
        { signal },
      );

    zoomImage
      ?.addEventListener(
        'keydown',
        (event) => {
          if (
            event.key !== 'Enter'
            && event.key !== ' '
          ) {
            return;
          }

          event.preventDefault();

          toggleZoomMagnification();
        },
        { signal },
      );

    if (zoomPrevious) {
      zoomPrevious.hidden =
        imageThumbs.length < 2;
    }

    if (zoomNext) {
      zoomNext.hidden =
        imageThumbs.length < 2;
    }

    zoomDialog
      ?.addEventListener(
        'click',
        (event) => {
          if (
            event.target
            === zoomDialog
          ) {
            zoomDialog.close();
          }
        },
        { signal },
      );

    zoomDialog
      ?.addEventListener(
        'keydown',
        (event) => {
          if (
            event.key
            === 'ArrowLeft'
          ) {
            stepZoom(-1);
          }

          if (
            event.key
            === 'ArrowRight'
          ) {
            stepZoom(1);
          }
        },
        { signal },
      );

    zoomDialog
      ?.addEventListener(
        'close',
        () =>
          setZoomMagnified(
            false,
          ),
        { signal },
      );

    let touchStartX = 0;

    const beginSwipe = (
      event,
    ) => {
      touchStartX =
        event.changedTouches[0]
          ?.clientX
        || 0;
    };

    const completeSwipe = (
      event,
      callback,
    ) => {
      const distance =
        (
          event.changedTouches[0]
            ?.clientX
          || 0
        )
        - touchStartX;

      if (
        Math.abs(distance)
        > 50
      ) {
        callback(
          distance > 0
            ? -1
            : 1,
        );
      }
    };

    zoomStage
      ?.addEventListener(
        'touchstart',
        beginSwipe,
        {
          passive: true,
          signal,
        },
      );

    zoomStage
      ?.addEventListener(
        'touchend',
        (event) =>
          completeSwipe(
            event,
            stepZoom,
          ),
        {
          passive: true,
          signal,
        },
      );

    mediaStage
      ?.addEventListener(
        'touchstart',
        beginSwipe,
        {
          passive: true,
          signal,
        },
      );

    mediaStage
      ?.addEventListener(
        'touchend',
        (event) =>
          completeSwipe(
            event,
            stepMedia,
          ),
        {
          passive: true,
          signal,
        },
      );

    if (
      activeThumb?.dataset
        .mediaType === 'image'
    ) {
      activeImageData =
        imageDataFromThumb(
          activeThumb,
        );
    }

    updateMediaCounters();
  };

  const loadRecommendations = (
    section,
  ) => {
    if (
      !section
      || section.dataset
        .ksLoaded === 'true'
    ) {
      return;
    }

    section.dataset.ksLoaded =
      'true';

    const url =
      section.dataset
        .ksRecommendationsUrl;

    if (!url) return;

    fetch(url, {
      headers: {
        Accept: 'text/html',
      },
      credentials:
        'same-origin',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `HTTP ${response.status}`,
          );
        }

        return response.text();
      })
      .then((html) => {
        const fetchedDocument =
          new DOMParser()
            .parseFromString(
              html,
              'text/html',
            );

        const replacement =
          fetchedDocument
            .querySelector(
              '[data-ks-recommendations]',
            );

        if (
          replacement
          && replacement.innerHTML
            .trim()
        ) {
          section.replaceWith(
            replacement,
          );
        } else {
          section.remove();
        }
      })
      .catch(() => {
        section.remove();
      });
  };

  const initRecommendations = (
    scope = document,
  ) => {
    scope
      .querySelectorAll?.(
        '[data-ks-recommendations]',
      )
      .forEach((section) => {
        if (
          !section.dataset
            .ksRecommendationsUrl
          || section.dataset
            .ksLoaded === 'true'
        ) {
          return;
        }

        if (
          !(
            'IntersectionObserver'
            in window
          )
        ) {
          loadRecommendations(
            section,
          );

          return;
        }

        const observer =
          new IntersectionObserver(
            (entries) => {
              if (
                !entries[0]
                  ?.isIntersecting
              ) {
                return;
              }

              observer.disconnect();

              loadRecommendations(
                section,
              );
            },
            {
              rootMargin:
                '300px 0px',
            },
          );

        observer.observe(
          section,
        );
      });
  };

  const init = (
    scope = document,
  ) => {
    scope
      .querySelectorAll?.(
        '[data-ks-product-root]',
      )
      .forEach(initProduct);

    initRecommendations(
      scope,
    );
  };

  if (
    document.readyState
    === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => init(),
      { once: true },
    );
  } else {
    init();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) =>
      init(event.target),
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      event.target
        .querySelectorAll?.(
          '[data-ks-product-root]',
        )
        .forEach((root) => {
          root
            .ksAbortController
            ?.abort();
        });
    },
  );
})();